import { useEffect, useState } from 'react';
import { ApiError, api } from '../api/client';
import type { GuestWishlistItem } from '../api/types';
import { Alert, LoadingBlock } from '../components/ui';

type Svar = { items: GuestWishlistItem[] };

/**
 * Ønskelista slik gjesten ser den, bak sin egen svarlenke.
 *
 * Poenget med å reservere er at de andre gjestene skal slippe å kjøpe det samme.
 * Derfor står «Reservert» tydelig på kortet, mens hvem som tok den, ikke vises
 * for noen – heller ikke for brudeparet.
 */
export function GaveListe({
  token,
  forhandsvisningFor,
}: {
  token: string;
  /** Satt når brudeparet ser på sin egen forhåndsvisning, uten svarlenke. */
  forhandsvisningFor?: string;
}) {
  const [items, setItems] = useState<GuestWishlistItem[] | null>(null);
  const [feil, setFeil] = useState<string | null>(null);
  const [jobber, setJobber] = useState<string | null>(null);

  useEffect(() => {
    let gjelder = true;
    /*
      I forhåndsvisningen hentes lista fra brudeparets eget endepunkt, og det
      sier ingenting om reservasjoner – det er hele poenget med det. Gavene
      står derfor som ledige her, uansett hva gjestene har tatt.
    */
    const hent = forhandsvisningFor
      ? api
          .get<{ items: { id: string; title: string; notes: string | null; url: string | null; hasImage: boolean }[] }>(
            `/weddings/${forhandsvisningFor}/onskeliste`,
          )
          .then((svar) => ({
            items: svar.items.map((item) => ({ ...item, reserved: false, reservedByMe: false })),
          }))
      : api.get<Svar>(`/rsvp/${token}/onskeliste`);

    hent
      .then((svar) => gjelder && setItems(svar.items))
      .catch((caught) => {
        if (!gjelder) return;
        // Et par uten Standard har ikke verktøyet, og da har de heller ingen
        // ønsker. Som for programmet: si det, ikke vis en feilmelding.
        if (forhandsvisningFor && caught instanceof ApiError && caught.isPlanRequired) {
          setItems([]);
          return;
        }
        setFeil(caught instanceof ApiError ? caught.message : 'Klarte ikke å hente ønskelista.');
      });
    return () => {
      gjelder = false;
    };
  }, [token, forhandsvisningFor]);

  const settReservasjon = async (item: GuestWishlistItem, reserver: boolean) => {
    // I forhåndsvisningen finnes ingen gjest å reservere på vegne av.
    if (forhandsvisningFor) {
      setFeil('Dette er en forhåndsvisning. Reservasjonen lagres ikke.');
      return;
    }
    setJobber(item.id);
    setFeil(null);
    try {
      const sti = `/rsvp/${token}/onskeliste/${item.id}/reservasjon`;
      const svar = reserver
        ? await api.post<{ item: GuestWishlistItem }>(sti)
        : await api.del<{ item: GuestWishlistItem }>(sti);
      setItems((forrige) =>
        (forrige ?? []).map((gammelt) => (gammelt.id === item.id ? svar.item : gammelt)),
      );
    } catch (caught) {
      setFeil(caught instanceof ApiError ? caught.message : 'Det gikk ikke. Prøv igjen.');
      // Noen andre kan ha rukket den først; da er lista utdatert.
      try {
        setItems((await api.get<Svar>(`/rsvp/${token}/onskeliste`)).items);
      } catch {
        /* Beholder det vi har. Feilmeldingen over sier fra. */
      }
    } finally {
      setJobber(null);
    }
  };

  if (feil && !items) return <Alert kind="error">{feil}</Alert>;
  if (!items) return <LoadingBlock label="Henter ønskelista …" />;

  if (items.length === 0) {
    return (
      <p className="muted" style={{ textAlign: 'center' }}>
        Brudeparet har ikke lagt inn noen ønsker ennå. Se innom igjen senere.
      </p>
    );
  }

  return (
    <>
      <p className="small muted" style={{ marginTop: 0 }}>
        Ønsker du å kjøpe en av gavene, trykk «Jeg tar denne». Da ser de andre gjestene at
        den er reservert – men ikke av hvem, og brudeparet får ikke vite at den er
        reservert.
      </p>

      {forhandsvisningFor ? (
        <p className="small muted">
          I forhåndsvisningen står alle gavene som ledige. Dere skal ikke se hvem som har
          tatt hva – det er hele poenget.
        </p>
      ) : null}

      {feil ? <Alert kind="error">{feil}</Alert> : null}

      <ul className="onske-rutenett">
        {items.map((item) => (
          <li
            className={`onske-kort${item.reserved && !item.reservedByMe ? ' er-tatt' : ''}`}
            key={item.id}
          >
            <div className="onske-bilde">
              {item.hasImage ? (
                <img src={`/api/onskeliste-bilder/${item.id}`} alt="" loading="lazy" />
              ) : (
                <span className="onske-uten-bilde" aria-hidden="true">
                  🎁
                </span>
              )}
              {item.reserved ? (
                <span className="onske-merke">
                  {item.reservedByMe ? 'Du tar denne' : 'Reservert'}
                </span>
              ) : null}
            </div>
            <div className="onske-tekst">
              <h3>{item.title}</h3>
              {item.notes ? <p className="small muted">{item.notes}</p> : null}
              {item.url ? (
                <a
                  className="small onske-lenke"
                  href={item.url}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Se i nettbutikken
                </a>
              ) : null}
            </div>
            <div className="onske-knapper">
              {item.reservedByMe ? (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  disabled={jobber === item.id}
                  onClick={() => void settReservasjon(item, false)}
                >
                  Angre
                </button>
              ) : item.reserved ? (
                <span className="small muted">Noen andre tar denne</span>
              ) : (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={jobber === item.id}
                  onClick={() => void settReservasjon(item, true)}
                >
                  {jobber === item.id ? 'Et øyeblikk …' : 'Jeg tar denne'}
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}
